import React, { useState } from "react"
import { asDialogData } from "../../../core/utils/utils"
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, List, ListItem, ListItemText,
    MenuItem, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { format } from "date-fns"
import { getDateInServerTimeZone } from "../../../core/utils/utils"
import {
    useGetAllExperienceRequestsQuery,
    useGetAllExperienceCategoriesQuery,
    useApproveExperienceRequestMutation,
    useRejectExperienceRequestMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"
import AddIcon from "@mui/icons-material/Add";
import {Link} from "react-router-dom";

const STATUS_CHIP = {
    WAITING: { label: "Waiting", color: "warning" },
    APPROVED: { label: "Approved", color: "success" },
    REJECTED: { label: "Rejected", color: "error" },
}

export default function ExperienceRequestsAdmin() {
    const { data: requests = [], isLoading } = useGetAllExperienceRequestsQuery({})
    const { data: categories = [] } = useGetAllExperienceCategoriesQuery()
    const [approveReq, approveResp] = useApproveExperienceRequestMutation()
    const [rejectReq, rejectResp] = useRejectExperienceRequestMutation()

    const [selected, setSelected] = useState(null)
    const [overrideCategoryId, setOverrideCategoryId] = useState("")
    const [adminComment, setAdminComment] = useState("")
    const [respDialogOpen, setRespDialogOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openDetails(row) {
        setSelected(row)
        setOverrideCategoryId(row.categoryId || "")
        setAdminComment("")
    }
    function close() { setSelected(null) }

    async function doApprove() {
        const override = overrideCategoryId && overrideCategoryId !== selected.categoryId
            ? overrideCategoryId
            : null
        const resp = await approveReq({
            requestId: selected.id,
            categoryId: override,
            adminComment,
        })
        setActionResp(asDialogData(resp))
        setRespDialogOpen(true)
        if (!resp.error) close()
    }
    async function doReject() {
        const resp = await rejectReq({ requestId: selected.id, adminComment })
        setActionResp(asDialogData(resp))
        setRespDialogOpen(true)
        if (!resp.error) close()
    }

    const rows = requests.map(r => ({
        ...r,
        rowId: r.id,
        itemsCount: r.items.length,
        totalXp: r.items.reduce((s, i) => s + (i.xp || 0), 0),
    }))

    const columns = [
        { field: "createdAt", headerName: "Created", width: 160, renderCell: (p) => {
            try { return format(getDateInServerTimeZone(p.value), "dd.MM.yyyy HH:mm") } catch (e) { return p.value }
        }},
        { field: "authorName", headerName: "Author", width: 180 },
        { field: "categoryName", headerName: "Category", width: 130 },
        { field: "itemsCount", headerName: "People", width: 80 },
        { field: "totalXp", headerName: "Total XP", width: 100 },
        { field: "comment", headerName: "Comment", flex: 1 },
        { field: "status", headerName: "Status", width: 160, renderCell: (p) => {
            const c = STATUS_CHIP[p.value] || { label: p.value, color: "default" }
            return <Chip size="small" label={c.label} color={c.color} />
        }},
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>XP accrual requests</Typography>
                <Button variant="contained" color="warning"
                        startIcon={<AddIcon />}
                        component={Link}
                        to="/admin/experience/request/new"
                >
                    Mass charge
                </Button>
            </Stack>
            <Paper sx={{ height: 640 }}>
                <DataGrid
                    rows={rows}
                    getRowId={r => r.rowId}
                    columns={columns}
                    onRowClick={(p) => openDetails(p.row)}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!selected} onClose={close} maxWidth="md" fullWidth>
                {selected && (
                    <>
                        <DialogTitle>Request by {selected.authorName}</DialogTitle>
                        <DialogContent>
                            <Stack spacing={2}>
                                <Typography variant="body2" color="text.secondary">
                                    Created: {(() => { try { return format(getDateInServerTimeZone(selected.createdAt), "dd.MM.yyyy HH:mm") } catch (e) { return selected.createdAt } })()}  · Status: {STATUS_CHIP[selected.status]?.label || selected.status}
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                                    <Typography variant="caption" color="text.secondary">Author comment:</Typography>
                                    <Typography variant="body1">{selected.comment}</Typography>
                                </Paper>

                                <TextField
                                    select
                                    label="Category (can override)"
                                    value={overrideCategoryId}
                                    onChange={e => setOverrideCategoryId(e.target.value)}
                                    disabled={selected.status !== "WAITING"}
                                >
                                    {categories.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </TextField>

                                <Typography variant="subtitle2">Participants</Typography>
                                <List dense>
                                    {selected.items.map((it, i) => (
                                        <ListItem key={i} divider>
                                            <ListItemText
                                                primary={<><strong>{it.fullName || it.email}</strong> — {it.xp} XP</>}
                                                secondary={it.email}
                                            />
                                        </ListItem>
                                    ))}
                                </List>

                                {selected.status === "WAITING" && (
                                    <TextField
                                        label="Admin comment"
                                        value={adminComment}
                                        onChange={e => setAdminComment(e.target.value)}
                                        multiline
                                        minRows={2}
                                    />
                                )}

                                {selected.adminComment && (
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Admin comment:</Typography>
                                        <Typography variant="body2">{selected.adminComment}</Typography>
                                    </Paper>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={close}>Close</Button>
                            {selected.status === "WAITING" && (
                                <>
                                    <Button color="error" onClick={doReject} disabled={rejectResp.isLoading}>
                                        Reject
                                    </Button>
                                    <Button variant="contained" color="warning" onClick={doApprove} disabled={approveResp.isLoading}>
                                        Approve
                                    </Button>
                                </>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <ResponseDialog
                open={respDialogOpen}
                onClose={() => setRespDialogOpen(false)}
                responseData={actionResp}
                label="Request action"
                information="Done"
            />
        </Box>
    )
}
